 
/**
 * Authentication Page Component
 * 
 * This component provides a complete authentication interface
 * that combines login, registration, and TOTP verification.
 */

import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { 
  Box, 
  Container, 
  Typography, 
  Fade, 
  Slide, 
  Card, 
  CardContent,
  Divider,
  Chip,
  CircularProgress,
  useTheme,
  alpha
} from '@mui/material';
import { useAuth } from './AuthProvider';
import LoginForm from './LoginForm';
import TOTPInput from './TOTPInput';
import ForgotPasswordForm, { ForgotPasswordStep } from './ForgotPasswordForm';

const SHOULD_DEBUG = process.env.NODE_ENV !== 'production' && process.env.REACT_APP_ENABLE_DEBUG_LOGGING === 'true';

interface AuthPageProps {
  onAuthSuccess?: () => void;
  className?: string;
  defaultMode?: 'login' | 'totp' | 'forgotPassword';
}

const AuthPage: React.FC<AuthPageProps> = ({ 
  onAuthSuccess, 
  className = '',
  defaultMode = 'login'
}) => {
  const { isAuthenticated, totpRequired, user, permissions, generateTOTP, clearError, totpSessionId, passwordResetToken } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const rawInviteEmail = (location.state as { inviteLoginEmail?: string } | null)?.inviteLoginEmail;
  const inviteLoginEmail =
    typeof rawInviteEmail === 'string' && rawInviteEmail.trim() ? rawInviteEmail.trim() : undefined;
  const [mode, setModeInternal] = useState<'login' | 'totp' | 'forgotPassword'>('login');

  // Wrapper to track mode changes
  const setMode = (newMode: 'login' | 'totp' | 'forgotPassword') => {
    console.log('AuthPage - setMode called:', newMode, 'from:', mode, new Error().stack);
    setModeInternal(newMode);
  };
  const [userEmail, setUserEmail] = useState<string>('');
  const [userPassword, setUserPassword] = useState<string>('');
  const [showSuccessMessage, setShowSuccessMessage] = useState<boolean>(false);
  const [isTransitioning, setIsTransitioning] = useState<boolean>(false);
  const [resendError, setResendError] = useState<string | null>(null);
  // Lifted state for ForgotPasswordForm to persist across unmounts
  const [forgotPasswordStep, setForgotPasswordStep] = useState<ForgotPasswordStep>('email');

  // Automatically advance to TOTP step when password reset token is received
  useEffect(() => {
    console.log('AuthPage - Password reset effect check:', {
      hasToken: !!passwordResetToken,
      mode,
      forgotPasswordStep
    });
    if (passwordResetToken && mode === 'forgotPassword' && forgotPasswordStep === 'email') {
      console.log('AuthPage - Password reset token received, advancing to TOTP step');
      setForgotPasswordStep('totp');
    }
  }, [passwordResetToken, mode, forgotPasswordStep]);

  // Debug: Track AuthPage mount/unmount
  useEffect(() => {
    console.log('AuthPage - MOUNTED');
    return () => {
      console.log('AuthPage - UNMOUNTING');
    };
  }, []);

  // Use ref to persist password across re-renders
  const passwordRef = useRef<string>('');

  // Debug: Track password state changes
  useEffect(() => {
    console.log('AuthPage - Password state changed:', { hasPassword: !!userPassword, userEmail });
  }, [userPassword, userEmail]);

  // Redirect if already authenticated
  useEffect(() => {
    if (isAuthenticated && onAuthSuccess) {
      onAuthSuccess();
    }
  }, [isAuthenticated, onAuthSuccess]);

  // Check for admin redirect after authentication
  useEffect(() => {
    if (isAuthenticated && user) {
      if (SHOULD_DEBUG) console.log('AuthPage - User authenticated, checking admin status');
      // Check if user has admin role
      const hasAdminPermissions = (user: any): boolean => {
        return user?.role === 'admin';
      };
      
      if (hasAdminPermissions(user)) {
        if (SHOULD_DEBUG) console.log('AuthPage - User has admin permissions, redirecting to admin dashboard');
        navigate('/admin');
      } else {
        if (SHOULD_DEBUG) console.log('AuthPage - User does not have admin permissions, redirecting to main dashboard');
        navigate('/');
      }
    }
  }, [isAuthenticated, user, permissions, navigate]);

  // Handle TOTP requirement
  useEffect(() => {
    if (SHOULD_DEBUG) console.log('AuthPage - TOTP effect triggered:', { totpRequired, user, userEmail, hasPassword: !!userPassword });
    if (totpRequired) {
      setIsTransitioning(true);
      setTimeout(() => {
        setMode('totp');
        // Set the user email for TOTP verification (only if not already set)
        if (user?.email && !userEmail) {
          if (SHOULD_DEBUG) console.log('AuthPage - Setting user email from user object:', user.email);
          setUserEmail(user.email);
        } else {
          if (SHOULD_DEBUG) console.log('AuthPage - Email already set or not available', { userEmail, userEmailFromUser: user?.email });
        }
        // Preserve password - don't clear it
        if (SHOULD_DEBUG) console.log('AuthPage - Password state after TOTP mode switch:', { hasPassword: !!userPassword });
        setIsTransitioning(false);
      }, 300);
    }
    // Intentionally omit userEmail/userPassword from deps to avoid re-triggering transitions while typing.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [totpRequired, user]);


  // Handle login attempt (capture email and password for TOTP resend)
  const handleLoginAttempt = (email: string, password: string) => {
    console.log('AuthPage - handleLoginAttempt called', { email, hasPassword: !!password, passwordLength: password?.length });
    setUserEmail(email);
    setUserPassword(password);
    passwordRef.current = password; // Store in ref as backup
    console.log('AuthPage - Password stored for resend', { statePassword: !!userPassword, refPassword: !!passwordRef.current });
  };

  // Handle successful login
  const handleLoginSuccess = () => {
    if (onAuthSuccess) {
      onAuthSuccess();
    }
  };

  // Handle TOTP verification success
  const handleTOTPSuccess = () => {
    // Clear stored password after successful TOTP verification
    console.log('AuthPage - TOTP verification successful, clearing password');
    setUserPassword('');
    passwordRef.current = ''; // Clear ref as well
    if (onAuthSuccess) {
      onAuthSuccess();
    }
  };

  // Handle TOTP resend
  const handleResendTOTP = async () => {
    console.log('AuthPage - Resend TOTP called', { 
      userEmail, 
      hasTempToken: !!totpSessionId,
      hasStatePassword: !!userPassword,
      hasRefPassword: !!passwordRef.current
    });
    
    if (!userEmail) {
      console.error('AuthPage - Missing email for resend');
      setResendError('Unable to resend code. Please try logging in again.');
      return;
    }

    setResendError(null);
    clearError(); // Clear any previous errors
    
    console.log('AuthPage - Calling generateTOTP with temp_token...');
    // Use temp_token (preferred) or fallback to password
    const passwordToUse = userPassword || passwordRef.current;
    const result = await generateTOTP(userEmail, passwordToUse || undefined, totpSessionId || undefined);
    console.log('AuthPage - Generate TOTP result:', result);
    
    if (result.success) {
      // Success - code sent, error will be cleared by generateTOTP
      if (SHOULD_DEBUG) console.log('AuthPage - TOTP code resent successfully');
    } else {
      console.error('AuthPage - TOTP resend failed:', result.error);
      setResendError(result.error || 'Failed to resend code. Please try again.');
    }
  };

  // Handle mode change
  const handleModeChange = (newMode: 'login' | 'totp' | 'forgotPassword') => {
    setIsTransitioning(true);
    setTimeout(() => {
      setMode(newMode);
      setShowSuccessMessage(false);
      // Reset forgot password step when leaving that mode
      if (newMode !== 'forgotPassword') {
        setForgotPasswordStep('email');
      }
      setIsTransitioning(false);
    }, 300);
  };

  // Handle forgot password navigation
  const handleForgotPassword = (email: string) => {
    setUserEmail(email);
    handleModeChange('forgotPassword');
  };

  // Handle forgot password success
  const handleForgotPasswordSuccess = () => {
    setShowSuccessMessage(true);
    setTimeout(() => {
      handleModeChange('login');
      setShowSuccessMessage(false);
    }, 2000);
  };

  const theme = useTheme();

  // If already authenticated, don't show auth page
  if (isAuthenticated) {
    return null;
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'linear-gradient(135deg, rgba(76, 175, 80, 0.1) 0%, rgba(48, 171, 240, 0.1) 100%)',
        position: 'relative',
        overflow: 'hidden',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'linear-gradient(180deg, rgba(233, 233, 233, 0.08) 0%, rgba(233, 233, 233, 0.08) 55%, rgba(169, 255, 178, 1) 100%)',
          zIndex: -1,
        }
      }}
    >
      {/* Floating background elements */}
      <Box
        sx={{
          position: 'absolute',
          top: '10%',
          left: '10%',
          width: 200,
          height: 200,
          borderRadius: '50%',
          background: `linear-gradient(45deg, ${alpha(theme.palette.primary.main, 0.1)}, ${alpha(theme.palette.secondary.main, 0.1)})`,
          animation: 'float 6s ease-in-out infinite',
          zIndex: 0,
        }}
      />
      <Box
        sx={{
          position: 'absolute',
          bottom: '15%',
          right: '15%',
          width: 150,
          height: 150,
          borderRadius: '50%',
          background: `linear-gradient(45deg, ${alpha(theme.palette.secondary.main, 0.1)}, ${alpha(theme.palette.primary.main, 0.1)})`,
          animation: 'float 8s ease-in-out infinite reverse',
          zIndex: 0,
        }}
      />

      <Container maxWidth="sm" sx={{ position: 'relative', zIndex: 1 }}>
        <Fade in timeout={800}>
          <Card
            elevation={24}
            sx={{
              borderRadius: 4,
              overflow: 'hidden',
              background: alpha(theme.palette.background.paper, 0.95),
              backdropFilter: 'blur(20px)',
              border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
              boxShadow: `0 25px 50px -12px ${alpha(theme.palette.primary.main, 0.25)}`,
            }}
          >
            {/* Header Section */}
            <Box
              sx={{
                background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
                color: '#ffffff',
                p: 4,
                textAlign: 'center',
                position: 'relative',
                '&::after': {
                  content: '""',
                  position: 'absolute',
                  bottom: 0,
                  left: 0,
                  right: 0,
                  height: 20,
                  background: `linear-gradient(to bottom, transparent, ${alpha(theme.palette.background.paper, 0.1)})`,
                }
              }}
            >
              <Typography
                variant="h3"
                component="h1"
                sx={{
                  fontWeight: 700,
                  mb: 1,
                  textShadow: '0 2px 4px rgba(0,0,0,0.1)',
                  background: 'linear-gradient(45deg, #ffffff, #f0f0f0)',
                  backgroundClip: 'text',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                }}
              >
                Productive Capabilities Data Lab
              </Typography>
              <Typography
                variant="h6"
                sx={{
                  opacity: 0.9,
                  fontWeight: 400,
                  mb: 2,
                }}
              >
                {mode === 'login' && 'Welcome back! Sign in to continue'}
                {mode === 'totp' && 'Two-Factor Authentication'}
                {mode === 'forgotPassword' && 'Reset Your Password'}
              </Typography>
              
              {/* Mode indicator chips */}
              <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center', flexWrap: 'wrap' }}>
                <Chip
                  label={mode === 'forgotPassword' ? 'Reset Password' : 'Login'}
                  size="small"
                  color="default"
                  variant="filled"
                  sx={{
                    color: theme.palette.primary.main,
                    backgroundColor: '#ffffff',
                  }}
                />
                {mode === 'totp' && (
                  <Chip
                    label="2FA"
                    size="small"
                    color="default"
                    variant="filled"
                    sx={{
                      color: theme.palette.primary.main,
                      backgroundColor: '#ffffff',
                    }}
                  />
                )}
              </Box>
            </Box>

            {/* Content Section */}
            <CardContent sx={{ p: 4 }}>
              {showSuccessMessage && (
                <Slide direction="down" in={showSuccessMessage} timeout={500}>
                  <Box
                    sx={{
                      p: 3,
                      mb: 3,
                      borderRadius: 2,
                      background: `linear-gradient(135deg, ${alpha(theme.palette.success.main, 0.1)}, ${alpha(theme.palette.success.main, 0.05)})`,
                      border: `1px solid ${alpha(theme.palette.success.main, 0.2)}`,
                      textAlign: 'center',
                    }}
                  >
                    <Typography variant="h6" color="success.main" sx={{ fontWeight: 600, mb: 1 }}>
                      Password Reset Successful!
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Your password has been reset. Please sign in with your new password.
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                      Redirecting to login...
                    </Typography>
                  </Box>
                </Slide>
              )}

              <Box sx={{ position: 'relative', minHeight: 400 }}>
                {/* Loading indicator during transitions */}
                {isTransitioning && (
                  <Box
                    sx={{
                      position: 'absolute',
                      top: '50%',
                      left: '50%',
                      transform: 'translate(-50%, -50%)',
                      zIndex: 10,
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      gap: 2,
                    }}
                  >
                    <CircularProgress size={40} />
                    <Typography variant="body2" color="text.secondary">
                      {mode === 'login' ? 'Switching to TOTP verification...' : 'Returning to login...'}
                    </Typography>
                  </Box>
                )}

                <Fade in={mode === 'login' && !isTransitioning} timeout={500} unmountOnExit>
                  <Box sx={{ position: mode === 'login' ? 'static' : 'absolute', width: '100%', top: 0, left: 0 }}>
                    <LoginForm
                      initialEmail={inviteLoginEmail}
                      onSuccess={handleLoginSuccess}
                      onLoginAttempt={handleLoginAttempt}
                      onSwitchToRegister={() => {}} // Disabled
                      onForgotPassword={handleForgotPassword}
                    />
                  </Box>
                </Fade>

                <Fade in={mode === 'totp' && !isTransitioning} timeout={500} unmountOnExit>
                  <Box sx={{ position: mode === 'totp' ? 'static' : 'absolute', width: '100%', top: 0, left: 0 }}>
                    {resendError && (
                      <Box sx={{ mb: 2, p: 2, borderRadius: 2, bgcolor: 'error.light', color: 'error.contrastText' }}>
                        <Typography variant="body2">{resendError}</Typography>
                      </Box>
                    )}
                    <TOTPInput
                      email={userEmail}
                      onSuccess={handleTOTPSuccess}
                      onResend={handleResendTOTP}
                      onCancel={() => {
                        setUserPassword(''); // Clear password on cancel
                        passwordRef.current = ''; // Clear ref as well
                        handleModeChange('login');
                      }}
                    />
                  </Box>
                </Fade>

                <Fade in={mode === 'forgotPassword' && !isTransitioning} timeout={500} unmountOnExit>
                  <Box sx={{ position: mode === 'forgotPassword' ? 'static' : 'absolute', width: '100%', top: 0, left: 0 }}>
                    <ForgotPasswordForm
                      initialEmail={userEmail}
                      onSuccess={handleForgotPasswordSuccess}
                      onCancel={() => handleModeChange('login')}
                      step={forgotPasswordStep}
                      onStepChange={setForgotPasswordStep}
                    />
                  </Box>
                </Fade>
              </Box>

              {/* Minimal footer message below Sign In */}
              <Divider sx={{ my: 3 }} />
              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="body2" color="text.secondary">
                  If you need an account, or access to additional value chains or company data, please contact support@rubidiumdata.com.
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Fade>
      </Container>

      {/* Add floating animation keyframes */}
      <style>{`
        @keyframes float {
          0%, 100% { transform: translateY(0px) rotate(0deg); }
          50% { transform: translateY(-20px) rotate(180deg); }
        }
      `}</style>
    </Box>
  );
};

export default AuthPage;
